/* Customer portal — Login screen. */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import GadtechLogo from "@/components/GadtechLogo";
import PortalInstallPrompt from "@/components/PortalInstallPrompt";
import usePortalPWA from "@/hooks/usePortalPWA";

export default function PortalLogin() {
  const { login } = usePortalAuth();
  const nav = useNavigate();
  usePortalPWA();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(email.trim(), password);
      nav("/portal/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white grid lg:grid-cols-2" data-testid="portal-login">
      <div className="p-10 lg:p-16 flex flex-col justify-center max-w-xl w-full mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <div className="bg-white/95 rounded-lg px-3 py-2 shadow-lg shadow-orange-900/30">
            <GadtechLogo className="h-10" data-testid="portal-login-logo" />
          </div>
          <div className="text-[10px] tracking-[0.28em] text-neutral-400">CUSTOMER PORTAL</div>
        </div>

        <div className="text-[10px] tracking-[0.28em] text-neutral-400 mb-3">Secure Sign-in</div>
        <h1 className="text-5xl font-black leading-none mb-2">
          Track every
        </h1>
        <h1 className="text-5xl font-black text-primary-orange leading-none mb-6">
          load. live.
        </h1>
        <p className="text-neutral-300 mb-10 max-w-md">
          Place orders, watch trucks head to your site, approve dockets and download invoices — all in one place.
        </p>

        <form onSubmit={submit} className="space-y-5 max-w-md">
          <div>
            <Label className="text-[11px] tracking-widest text-neutral-400 uppercase">Email</Label>
            <Input
              data-testid="portal-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none bg-transparent border-neutral-700 text-white mono focus:border-primary-orange"
            />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest text-neutral-400 uppercase">Password</Label>
            <Input
              data-testid="portal-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-none bg-transparent border-neutral-700 text-white mono focus:border-primary-orange"
            />
          </div>
          <Button
            data-testid="portal-login-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-sm font-bold py-3"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="mt-8 text-sm text-neutral-400">
          New to the portal?{" "}
          <Link to="/portal/register" data-testid="portal-login-to-register" className="text-primary-orange hover:underline font-bold">
            Create an account
          </Link>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(10,10,11,0.55),rgba(10,10,11,0.85)), url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 h-full flex flex-col justify-end p-16 text-white">
          <div className="text-[10px] tracking-[0.28em] text-primary-orange mb-2">READY-MIX OPERATIONS</div>
          <div className="text-4xl font-black leading-tight mb-2">Precision from<br/>plant to pour.</div>
          <div className="text-neutral-300 text-sm max-w-sm">Real-time GPS · scheduled loads · signed dockets · account statements.</div>
        </div>
      </div>
      <PortalInstallPrompt />
    </div>
  );
}
